// Professional School Management System (SMS Pro)
// Version: 1.0 - localStorage Edition
// Easy to migrate to backend later

import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './App.css';

function App() {
  // ==================== LICENSE & AUTH STATE ====================
  const [isActivated, setIsActivated] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [licenseType, setLicenseType] = useState('DEMO'); // DEMO or PREMIUM

  // ==================== DATA STATE ====================
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [marks, setMarks] = useState([]);
  const [fees, setFees] = useState([]);
  const [classes, setClasses] = useState(['6th', '7th', '8th', '9th', '10th', '11th', '12th']);
  const [subjects, setSubjects] = useState(['English', 'Hindi', 'Math', 'Science', 'Social Science']);
  
  // School Configuration
  const [schoolConfig, setSchoolConfig] = useState({
    schoolName: 'SMS Pro',
    address: '',
    phone: '',
    email: '',
    website: ''
  });

  // ==================== MODAL & FORM STATE ====================
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // ==================== SEARCH & FILTER STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  // ==================== FORM DATA ====================
  const [studentForm, setStudentForm] = useState({
    name: '', fatherName: '', motherName: '', rollNo: '', class: '', section: 'A',
    dob: '', gender: 'Male', phone: '', email: '', address: '', admissionDate: '', 
    bloodGroup: '', photo: ''
  });

  const [teacherForm, setTeacherForm] = useState({
    name: '', subject: '', qualification: '', experience: '', phone: '', email: '',
    salary: '', joinDate: '', address: '', gender: 'Male'
  });

  // ==================== LOAD DATA ON MOUNT ====================
  useEffect(() => {
    checkLicense();
    loadAllData();
  }, []);

  // ==================== LICENSE MANAGEMENT ====================
  const checkLicense = () => {
    const license = localStorage.getItem('sms_license');
    const demoLicenses = ['SMS-PRO-2024-DEMO'];
    const premiumLicenses = ['SMS-PRO-2024-FULL']; // Add school-specific keys here
    
    if (license) {
      if (demoLicenses.includes(license)) {
        setIsActivated(true);
        setLicenseType('DEMO');
      } else if (premiumLicenses.includes(license)) {
        setIsActivated(true);
        setLicenseType('PREMIUM');
      }
    }
  };

  const activateLicense = (key) => {
    const demoLicenses = ['SMS-PRO-2024-DEMO'];
    const premiumLicenses = ['SMS-PRO-2024-FULL']; // Add school-specific keys here
    
    const upperKey = key.toUpperCase();
    
    if (demoLicenses.includes(upperKey)) {
      localStorage.setItem('sms_license', upperKey);
      setIsActivated(true);
      setLicenseType('DEMO');
      alert('✅ Demo license activated! (Limited features)');
    } else if (premiumLicenses.includes(upperKey)) {
      localStorage.setItem('sms_license', upperKey);
      setIsActivated(true);
      setLicenseType('PREMIUM');
      alert('✅ Premium license activated successfully!');
    } else {
      alert('❌ Invalid license key!');
    }
  };

  // ==================== DATA MANAGEMENT (localStorage API Layer) ====================
  const loadAllData = () => {
    // Load School Config
    const savedConfig = localStorage.getItem('sms_school_config');
    if (savedConfig) {
      setSchoolConfig(JSON.parse(savedConfig));
    }
    
    // Load Students
    const savedStudents = localStorage.getItem('sms_students');
    if (savedStudents) {
      setStudents(JSON.parse(savedStudents));
    } else {
      // Demo data
      const demoStudents = [
        { 
          id: 1, name: 'Rahul Sharma', fatherName: 'Rajesh Sharma', motherName: 'Sunita Sharma',
          rollNo: '101', class: '10th', section: 'A', dob: '2009-05-15', gender: 'Male',
          phone: '9876543210', email: 'rahul@email.com', address: 'Delhi', 
          admissionDate: '2020-04-01', bloodGroup: 'O+', photo: ''
        },
        { 
          id: 2, name: 'Priya Singh', fatherName: 'Ramesh Singh', motherName: 'Kavita Singh',
          rollNo: '102', class: '10th', section: 'A', dob: '2009-07-20', gender: 'Female',
          phone: '9876543211', email: 'priya@email.com', address: 'Mumbai', 
          admissionDate: '2020-04-01', bloodGroup: 'A+', photo: ''
        }
      ];
      setStudents(demoStudents);
      localStorage.setItem('sms_students', JSON.stringify(demoStudents));
    }

    // Load Teachers
    const savedTeachers = localStorage.getItem('sms_teachers');
    if (savedTeachers) {
      setTeachers(JSON.parse(savedTeachers));
    } else {
      const demoTeachers = [
        {
          id: 1, name: 'Dr. Anita Verma', subject: 'Math', qualification: 'M.Sc, B.Ed',
          experience: '10 years', phone: '9876543220', email: 'anita@school.com',
          salary: '45000', joinDate: '2015-06-01', address: 'Delhi', gender: 'Female'
        }
      ];
      setTeachers(demoTeachers);
      localStorage.setItem('sms_teachers', JSON.stringify(demoTeachers));
    }

    // Load other data
    const savedAttendance = localStorage.getItem('sms_attendance');
    if (savedAttendance) setAttendance(JSON.parse(savedAttendance));

    const savedMarks = localStorage.getItem('sms_marks');
    if (savedMarks) setMarks(JSON.parse(savedMarks));

    const savedFees = localStorage.getItem('sms_fees');
    if (savedFees) setFees(JSON.parse(savedFees));
  };

  const saveData = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  // ==================== USER AUTHENTICATION ====================
  const handleLogin = (username, password, role) => {
    // Demo credentials
    const users = {
      admin: { username: 'admin', password: 'admin123', role: 'admin', name: 'Admin' },
      teacher: { username: 'teacher', password: 'teacher123', role: 'teacher', name: 'Teacher' },
      student: { username: 'student', password: 'student123', role: 'student', name: 'Student' }
    };

    const user = Object.values(users).find(u => 
      u.username === username && u.password === password && u.role === role
    );

    if (user) {
      setCurrentUser(user);
      setIsLoggedIn(true);
      setActiveTab('dashboard');
    } else {
      alert('❌ Invalid credentials!');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setActiveTab('dashboard');
  };

  // ==================== STUDENT MANAGEMENT ====================
  const handleStudentSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      // Update existing
      const updated = students.map(s => s.id === editingItem.id ? { ...studentForm, id: editingItem.id } : s);
      setStudents(updated);
      saveData('sms_students', updated);
      alert('✅ Student updated successfully!');
    } else {
      // Add new
      const newStudent = { ...studentForm, id: Date.now() };
      const updated = [...students, newStudent];
      setStudents(updated);
      saveData('sms_students', updated);
      alert('✅ Student added successfully!');
    }
    
    resetStudentForm();
  };

  const handleDeleteStudent = (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      const updated = students.filter(s => s.id !== id);
      setStudents(updated);
      saveData('sms_students', updated);
      alert('✅ Student deleted!');
    }
  };

  const handleEditStudent = (student) => {
    setStudentForm(student);
    setEditingItem(student);
    setShowStudentModal(true);
  };

  const resetStudentForm = () => {
    setStudentForm({
      name: '', fatherName: '', motherName: '', rollNo: '', class: '', section: 'A',
      dob: '', gender: 'Male', phone: '', email: '', address: '', admissionDate: '', 
      bloodGroup: '', photo: ''
    });
    setEditingItem(null);
    setShowStudentModal(false);
  };

  // ==================== TEACHER MANAGEMENT ====================
  const handleTeacherSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      const updated = teachers.map(t => t.id === editingItem.id ? { ...teacherForm, id: editingItem.id } : t);
      setTeachers(updated);
      saveData('sms_teachers', updated);
      alert('✅ Teacher updated!');
    } else {
      const newTeacher = { ...teacherForm, id: Date.now() };
      const updated = [...teachers, newTeacher];
      setTeachers(updated);
      saveData('sms_teachers', updated);
      alert('✅ Teacher added!');
    }
    
    resetTeacherForm();
  };

  const handleDeleteTeacher = (id) => {
    if (window.confirm('Delete this teacher?')) {
      const updated = teachers.filter(t => t.id !== id);
      setTeachers(updated);
      saveData('sms_teachers', updated);
      alert('✅ Teacher deleted!');
    }
  };

  const handleEditTeacher = (teacher) => {
    setTeacherForm(teacher);
    setEditingItem(teacher);
    setShowTeacherModal(true);
  };

  const resetTeacherForm = () => {
    setTeacherForm({
      name: '', subject: '', qualification: '', experience: '', phone: '', email: '',
      salary: '', joinDate: '', address: '', gender: 'Male'
    });
    setEditingItem(null);
    setShowTeacherModal(false);
  };

  // ==================== ATTENDANCE MANAGEMENT ====================
  const markAttendance = (studentId, status, date) => {
    const record = {
      id: Date.now(),
      studentId,
      status, // Present, Absent, Late, Half-day
      date,
      markedBy: currentUser?.name || 'Admin'
    };
    
    // Remove existing record for same student & date
    const filtered = attendance.filter(a => !(a.studentId === studentId && a.date === date));
    const updated = [...filtered, record];
    
    setAttendance(updated);
    saveData('sms_attendance', updated);
  };

  const getAttendanceStatus = (studentId, date) => {
    const record = attendance.find(a => a.studentId === studentId && a.date === date);
    return record?.status || 'Not Marked';
  };

  // ==================== MARKS MANAGEMENT ====================
  const handleMarksSubmit = (studentId, subject, exam, marksObtained, totalMarks) => {
    const record = {
      id: Date.now(),
      studentId,
      subject,
      exam, // Unit Test, Mid Term, Final
      marksObtained: parseInt(marksObtained),
      totalMarks: parseInt(totalMarks),
      percentage: ((parseInt(marksObtained) / parseInt(totalMarks)) * 100).toFixed(2),
      grade: calculateGrade((parseInt(marksObtained) / parseInt(totalMarks)) * 100),
      date: new Date().toISOString().split('T')[0]
    };
    
    const updated = [...marks, record];
    setMarks(updated);
    saveData('sms_marks', updated);
    alert('✅ Marks saved!');
  };

  const calculateGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  // ==================== FEE MANAGEMENT ====================
  const handleFeePayment = (studentId, amount, month, paymentMode) => {
    const student = students.find(s => s.id === studentId);
    const totalAnnualFee = 50000; // Can be made dynamic
    
    // Calculate already paid
    const paidAmount = fees
      .filter(f => f.studentId === studentId)
      .reduce((sum, f) => sum + parseInt(f.amount), 0);
    
    const balance = totalAnnualFee - paidAmount;
    
    // Validation
    if (parseInt(amount) > balance) {
      alert(`❌ Cannot pay more than balance! Maximum: ₹${balance}`);
      return;
    }
    
    if (parseInt(amount) <= 0) {
      alert('❌ Invalid amount!');
      return;
    }
    
    const receipt = {
      id: Date.now(),
      receiptNo: `RCP${Date.now()}`,
      studentId,
      studentName: student?.name,
      amount: parseInt(amount),
      month,
      paymentMode, // Cash, Online, Cheque
      date: new Date().toISOString().split('T')[0],
      collectedBy: currentUser?.name || 'Admin'
    };
    
    const updated = [...fees, receipt];
    setFees(updated);
    saveData('sms_fees', updated);
    alert(`✅ Payment successful! Receipt No: ${receipt.receiptNo}`);
  };

  const getFeeStatus = (studentId) => {
    const totalFee = 50000;
    const paid = fees
      .filter(f => f.studentId === studentId)
      .reduce((sum, f) => sum + parseInt(f.amount), 0);
    const balance = totalFee - paid;
    
    return { totalFee, paid, balance, status: balance > 0 ? 'Pending' : 'Paid' };
  };

  // ==================== EXCEL IMPORT/EXPORT ====================
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const importFromExcel = (e, type) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const wb = XLSX.read(event.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      
      if (type === 'students') {
        const imported = data.map(d => ({ ...d, id: Date.now() + Math.random() }));
        const updated = [...students, ...imported];
        setStudents(updated);
        saveData('sms_students', updated);
        alert(`✅ ${imported.length} students imported!`);
      } else if (type === 'teachers') {
        const imported = data.map(d => ({ ...d, id: Date.now() + Math.random() }));
        const updated = [...teachers, ...imported];
        setTeachers(updated);
        saveData('sms_teachers', updated);
        alert(`✅ ${imported.length} teachers imported!`);
      }
    };
    
    reader.readAsBinaryString(file);
  };

  // ==================== RENDER FUNCTIONS ====================
  
  // License Activation Screen
  if (!isActivated) {
    return (
      <div className="activation-screen">
        <div className="activation-card">
          <h1>🔐 SMS Pro - License Activation</h1>
          <p>Enter your license key to activate the software</p>
          <form onSubmit={(e) => {
            e.preventDefault();
            const key = e.target.licenseKey.value;
            activateLicense(key);
          }}>
            <input 
              type="text" 
              name="licenseKey"
              placeholder="Enter License Key" 
              required 
            />
            <button type="submit">Activate</button>
          </form>
          <div className="demo-keys">
            <p><strong>Demo Keys:</strong></p>
            <code>SMS-PRO-2024-DEMO</code>
            <code>SMS-PRO-2024-FULL</code>
          </div>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>🏫 School Management System</h1>
          <h2>Welcome Back!</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            const username = e.target.username.value;
            const password = e.target.password.value;
            const role = e.target.role.value;
            handleLogin(username, password, role);
          }}>
            <select name="role" required>
              <option value="">Select Role</option>
              <option value="admin">Admin</option>
              <option value="teacher">Teacher</option>
              <option value="student">Student</option>
            </select>
            <input type="text" name="username" placeholder="Username" required />
            <input type="password" name="password" placeholder="Password" required />
            <button type="submit">Login</button>
          </form>
          <div className="demo-credentials">
            <p><strong>Demo Credentials:</strong></p>
            <p>Admin: admin / admin123</p>
            <p>Teacher: teacher / teacher123</p>
            <p>Student: student / student123</p>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  const filteredStudents = students.filter(s => 
    (s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     s.rollNo.includes(searchTerm)) &&
    (filterClass === '' || s.class === filterClass)
  );

  const filteredTeachers = teachers.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <h1>🏫 {schoolConfig.schoolName}</h1>
          <span className="user-info">Welcome, {currentUser.name} ({currentUser.role})</span>
        </div>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </header>

      {/* Navigation */}
      <nav className="nav-tabs">
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''} 
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        
        {/* Students Tab - Admin Only */}
        {currentUser.role === 'admin' && (
          <button 
            className={activeTab === 'students' ? 'active' : ''} 
            onClick={() => setActiveTab('students')}
          >
            👨‍🎓 Students
          </button>
        )}
        
        {/* Teachers Tab - Admin Only */}
        {currentUser.role === 'admin' && (
          <button 
            className={activeTab === 'teachers' ? 'active' : ''} 
            onClick={() => setActiveTab('teachers')}
          >
            👨‍🏫 Teachers
          </button>
        )}
        
        {/* Attendance Tab - Admin & Teacher */}
        {(currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <button 
            className={activeTab === 'attendance' ? 'active' : ''} 
            onClick={() => setActiveTab('attendance')}
          >
            ✅ Attendance
          </button>
        )}
        
        {/* Marks Tab - Admin & Teacher */}
        {(currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <button 
            className={activeTab === 'marks' ? 'active' : ''} 
            onClick={() => setActiveTab('marks')}
          >
            📝 Marks
          </button>
        )}
        
        {/* Fees Tab - Admin Only */}
        {currentUser.role === 'admin' && (
          <button 
            className={activeTab === 'fees' ? 'active' : ''} 
            onClick={() => setActiveTab('fees')}
          >
            💰 Fees
          </button>
        )}
        
        {/* Reports Tab - Admin & Teacher */}
        {(currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <button 
            className={activeTab === 'reports' ? 'active' : ''} 
            onClick={() => setActiveTab('reports')}
          >
            📄 Reports
          </button>
        )}
        
        {/* Settings Tab - Admin Only + Premium License */}
        {currentUser.role === 'admin' && licenseType === 'PREMIUM' && (
          <button 
            className={activeTab === 'settings' ? 'active' : ''} 
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </button>
        )}
      </nav>

      {/* Main Content */}
      <main className="main-content">
        
        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div className="dashboard">
            <h2>Dashboard Overview</h2>
            
            {/* Admin Dashboard */}
            {currentUser.role === 'admin' && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h3>Total Students</h3>
                    <p className="stat-number">{students.length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Total Teachers</h3>
                    <p className="stat-number">{teachers.length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Today's Attendance</h3>
                    <p className="stat-number">
                      {attendance.filter(a => a.date === filterDate && a.status === 'Present').length}
                    </p>
                  </div>
                  <div className="stat-card">
                    <h3>Fee Collection (Monthly)</h3>
                    <p className="stat-number">
                      ₹{fees
                        .filter(f => f.date.startsWith(new Date().toISOString().slice(0, 7)))
                        .reduce((sum, f) => sum + f.amount, 0)
                        .toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="quick-actions">
                  <h3>Quick Actions</h3>
                  <button onClick={() => { setActiveTab('students'); setShowStudentModal(true); }}>
                    ➕ Add New Student
                  </button>
                  <button onClick={() => { setActiveTab('teachers'); setShowTeacherModal(true); }}>
                    ➕ Add New Teacher
                  </button>
                  <button onClick={() => setActiveTab('attendance')}>
                    ✅ Mark Attendance
                  </button>
                  <button onClick={() => setActiveTab('fees')}>
                    💰 Collect Fee
                  </button>
                </div>
              </>
            )}

            {/* Teacher Dashboard */}
            {currentUser.role === 'teacher' && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h3>Total Students</h3>
                    <p className="stat-number">{students.length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Today's Attendance</h3>
                    <p className="stat-number">
                      {attendance.filter(a => a.date === filterDate && a.status === 'Present').length}
                    </p>
                  </div>
                  <div className="stat-card">
                    <h3>Pending Marks</h3>
                    <p className="stat-number">{students.length * subjects.length - marks.length}</p>
                  </div>
                </div>

                <div className="quick-actions">
                  <h3>Quick Actions</h3>
                  <button onClick={() => setActiveTab('attendance')}>
                    ✅ Mark Attendance
                  </button>
                  <button onClick={() => { setActiveTab('marks'); setShowMarksModal(true); }}>
                    📝 Enter Marks
                  </button>
                </div>
              </>
            )}

            {/* Student Dashboard */}
            {currentUser.role === 'student' && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h3>My Attendance</h3>
                    <p className="stat-number">
                      {attendance.filter(a => a.status === 'Present').length} Days
                    </p>
                  </div>
                  <div className="stat-card">
                    <h3>Total Marks Entered</h3>
                    <p className="stat-number">{marks.length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Fee Status</h3>
                    <p className="stat-number">₹{(50000 - fees.reduce((sum, f) => sum + f.amount, 0)).toLocaleString()}</p>
                    <p style={{fontSize: '14px', marginTop: '5px'}}>Balance</p>
                  </div>
                </div>

                <div className="quick-actions">
                  <h3>My Information</h3>
                  <p>Welcome! You can view your attendance, marks, and fee status from your dashboard.</p>
                  <p style={{marginTop: '10px', color: '#666'}}>
                    For any queries, please contact the school administration.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* STUDENTS TAB - ADMIN ONLY */}
        {activeTab === 'students' && currentUser.role === 'admin' && (
          <div className="students-section">
            <div className="section-header">
              <h2>Student Management</h2>
              <div className="actions">
                <button onClick={() => setShowStudentModal(true)}>➕ Add Student</button>
                <button onClick={() => exportToExcel(students, 'students')}>📥 Export Excel</button>
                <label className="import-btn">
                  📤 Import Excel
                  <input type="file" accept=".xlsx,.xls" onChange={(e) => importFromExcel(e, 'students')} style={{display: 'none'}} />
                </label>
              </div>
            </div>

            <div className="filters">
              <input 
                type="text" 
                placeholder="🔍 Search by name or roll no..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Roll No</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Father Name</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map(student => (
                  <tr key={student.id}>
                    <td>{student.rollNo}</td>
                    <td>{student.name}</td>
                    <td>{student.class}-{student.section}</td>
                    <td>{student.fatherName}</td>
                    <td>{student.phone}</td>
                    <td>
                      <button className="btn-edit" onClick={() => handleEditStudent(student)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDeleteStudent(student.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TEACHERS TAB - ADMIN ONLY */}
        {activeTab === 'teachers' && currentUser.role === 'admin' && (
          <div className="teachers-section">
            <div className="section-header">
              <h2>Teacher Management</h2>
              <div className="actions">
                <button onClick={() => setShowTeacherModal(true)}>➕ Add Teacher</button>
                <button onClick={() => exportToExcel(teachers, 'teachers')}>📥 Export Excel</button>
              </div>
            </div>

            <div className="filters">
              <input 
                type="text" 
                placeholder="🔍 Search teacher..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Qualification</th>
                  <th>Phone</th>
                  <th>Salary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map(teacher => (
                  <tr key={teacher.id}>
                    <td>{teacher.name}</td>
                    <td>{teacher.subject}</td>
                    <td>{teacher.qualification}</td>
                    <td>{teacher.phone}</td>
                    <td>₹{parseInt(teacher.salary).toLocaleString()}</td>
                    <td>
                      <button className="btn-edit" onClick={() => handleEditTeacher(teacher)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDeleteTeacher(teacher.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ATTENDANCE TAB - ADMIN & TEACHER ONLY */}
        {activeTab === 'attendance' && (currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <div className="attendance-section">
            <div className="section-header">
              <h2>Attendance Management</h2>
              <input 
                type="date" 
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              />
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Roll No</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>Mark</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const status = getAttendanceStatus(student.id, filterDate);
                  return (
                    <tr key={student.id}>
                      <td>{student.rollNo}</td>
                      <td>{student.name}</td>
                      <td>{student.class}</td>
                      <td>
                        <span className={`status-badge ${status.toLowerCase().replace(' ', '-')}`}>
                          {status}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => markAttendance(student.id, 'Present', filterDate)} className="btn-present">P</button>
                        <button onClick={() => markAttendance(student.id, 'Absent', filterDate)} className="btn-absent">A</button>
                        <button onClick={() => markAttendance(student.id, 'Late', filterDate)} className="btn-late">L</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* MARKS TAB - ADMIN & TEACHER ONLY */}
        {activeTab === 'marks' && (currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <div className="marks-section">
            <div className="section-header">
              <h2>Marks Management</h2>
              <button onClick={() => setShowMarksModal(true)}>➕ Add Marks</button>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Subject</th>
                  <th>Exam</th>
                  <th>Marks</th>
                  <th>Percentage</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                {marks.map(mark => {
                  const student = students.find(s => s.id === mark.studentId);
                  return (
                    <tr key={mark.id}>
                      <td>{student?.name}</td>
                      <td>{mark.subject}</td>
                      <td>{mark.exam}</td>
                      <td>{mark.marksObtained}/{mark.totalMarks}</td>
                      <td>{mark.percentage}%</td>
                      <td><span className={`grade-badge grade-${mark.grade.replace('+', 'plus')}`}>{mark.grade}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* FEES TAB - ADMIN ONLY */}
        {activeTab === 'fees' && currentUser.role === 'admin' && (
          <div className="fees-section">
            <div className="section-header">
              <h2>Fee Management</h2>
              <button onClick={() => setShowFeeModal(true)}>💰 Collect Fee</button>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Roll No</th>
                  <th>Student Name</th>
                  <th>Total Fee</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const feeStatus = getFeeStatus(student.id);
                  return (
                    <tr key={student.id}>
                      <td>{student.rollNo}</td>
                      <td>{student.name}</td>
                      <td>₹{feeStatus.totalFee.toLocaleString()}</td>
                      <td>₹{feeStatus.paid.toLocaleString()}</td>
                      <td>₹{feeStatus.balance.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${feeStatus.status.toLowerCase()}`}>
                          {feeStatus.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* REPORTS TAB - ADMIN & TEACHER ONLY */}
        {activeTab === 'reports' && (currentUser.role === 'admin' || currentUser.role === 'teacher') && (
          <div className="reports-section">
            <h2>Reports & Analytics</h2>
            <div className="report-cards">
              
              {/* Student Report - Admin Only */}
              {currentUser.role === 'admin' && (
                <div className="report-card">
                  <h3>Student Report</h3>
                  <button onClick={() => exportToExcel(students, 'student_report')}>Download</button>
                </div>
              )}
              
              {/* Attendance Report - Admin & Teacher */}
              <div className="report-card">
                <h3>Attendance Report</h3>
                <button onClick={() => exportToExcel(attendance, 'attendance_report')}>Download</button>
              </div>
              
              {/* Fee Collection Report - Admin Only */}
              {currentUser.role === 'admin' && (
                <div className="report-card">
                  <h3>Fee Collection Report</h3>
                  <button onClick={() => exportToExcel(fees, 'fee_report')}>Download</button>
                </div>
              )}
              
              {/* Marks Report - Admin & Teacher */}
              <div className="report-card">
                <h3>Marks Report</h3>
                <button onClick={() => exportToExcel(marks, 'marks_report')}>Download</button>
              </div>
              
            </div>
          </div>
        )}

        {/* SETTINGS TAB - ADMIN ONLY + PREMIUM LICENSE */}
        {activeTab === 'settings' && currentUser.role === 'admin' && licenseType === 'PREMIUM' && (
          <div className="settings-section">
            <h2>⚙️ School Configuration</h2>
            
            <div style={{background: '#fff3cd', padding: '15px', borderRadius: '10px', marginBottom: '25px', border: '1px solid #ffc107'}}>
              <p style={{margin: 0, color: '#856404'}}>
                <strong>🔑 Premium Feature:</strong> Customize your school information and branding. Changes will be reflected across the entire system.
              </p>
            </div>

            <div style={{background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.08)', maxWidth: '800px'}}>
              <h3 style={{marginBottom: '20px', color: '#333'}}>🏫 School Information</h3>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const newConfig = {
                  schoolName: formData.get('schoolName'),
                  address: formData.get('address'),
                  phone: formData.get('phone'),
                  email: formData.get('email'),
                  website: formData.get('website')
                };
                setSchoolConfig(newConfig);
                localStorage.setItem('sms_school_config', JSON.stringify(newConfig));
                alert('✅ School settings saved successfully! Refresh to see changes in header.');
              }}>
                <div className="form-grid" style={{marginBottom: '20px'}}>
                  <div style={{gridColumn: '1 / -1'}}>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333'}}>
                      School Name *
                    </label>
                    <input 
                      type="text" 
                      name="schoolName" 
                      defaultValue={schoolConfig.schoolName}
                      placeholder="e.g. Delhi Public School"
                      required
                      style={{width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px'}}
                    />
                  </div>
                  
                  <div style={{gridColumn: '1 / -1'}}>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333'}}>
                      Address
                    </label>
                    <textarea 
                      name="address" 
                      defaultValue={schoolConfig.address}
                      placeholder="Full address of the school"
                      rows="3"
                      style={{width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px'}}
                    />
                  </div>
                  
                  <div>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333'}}>
                      Phone Number
                    </label>
                    <input 
                      type="tel" 
                      name="phone" 
                      defaultValue={schoolConfig.phone}
                      placeholder="e.g. 011-12345678"
                      style={{width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px'}}
                    />
                  </div>
                  
                  <div>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333'}}>
                      Email
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      defaultValue={schoolConfig.email}
                      placeholder="e.g. info@school.com"
                      style={{width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px'}}
                    />
                  </div>
                  
                  <div style={{gridColumn: '1 / -1'}}>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333'}}>
                      Website
                    </label>
                    <input 
                      type="url" 
                      name="website" 
                      defaultValue={schoolConfig.website}
                      placeholder="e.g. www.school.com"
                      style={{width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px'}}
                    />
                  </div>
                </div>

                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <button 
                    type="button" 
                    onClick={() => {
                      if (window.confirm('Reset to default values?')) {
                        setSchoolConfig({
                          schoolName: 'SMS Pro',
                          address: '',
                          phone: '',
                          email: '',
                          website: ''
                        });
                      }
                    }}
                    style={{padding: '12px 25px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'}}
                  >
                    ↺ Reset
                  </button>
                  <button 
                    type="submit"
                    style={{padding: '12px 25px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'}}
                  >
                    💾 Save Settings
                  </button>
                </div>
              </form>

              <div style={{marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '10px', borderLeft: '4px solid #667eea'}}>
                <h4 style={{margin: '0 0 10px 0', color: '#333'}}>📋 Current License Information</h4>
                <p style={{margin: '5px 0', color: '#666'}}>
                  <strong>Type:</strong> <span style={{color: licenseType === 'PREMIUM' ? '#28a745' : '#ffc107', fontWeight: '600'}}>{licenseType}</span>
                </p>
                <p style={{margin: '5px 0', color: '#666'}}>
                  <strong>Status:</strong> <span style={{color: '#28a745', fontWeight: '600'}}>Active</span>
                </p>
                {licenseType === 'DEMO' && (
                  <p style={{margin: '10px 0 0 0', color: '#856404', fontSize: '14px'}}>
                    ⚠️ Upgrade to Premium license to access Settings and White Labeling features.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

      </main>

      {/* MODALS */}
      
      {/* Student Modal */}
      {showStudentModal && (
        <div className="modal-overlay" onClick={() => resetStudentForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? 'Edit Student' : 'Add New Student'}</h2>
            <form onSubmit={handleStudentSubmit}>
              <div className="form-grid">
                <input type="text" placeholder="Full Name *" value={studentForm.name} 
                  onChange={(e) => setStudentForm({...studentForm, name: e.target.value})} required />
                <input type="text" placeholder="Father's Name *" value={studentForm.fatherName}
                  onChange={(e) => setStudentForm({...studentForm, fatherName: e.target.value})} required />
                <input type="text" placeholder="Mother's Name" value={studentForm.motherName}
                  onChange={(e) => setStudentForm({...studentForm, motherName: e.target.value})} />
                <input type="text" placeholder="Roll No *" value={studentForm.rollNo}
                  onChange={(e) => setStudentForm({...studentForm, rollNo: e.target.value})} required />
                <select value={studentForm.class} onChange={(e) => setStudentForm({...studentForm, class: e.target.value})} required>
                  <option value="">Select Class *</option>
                  {classes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={studentForm.section} onChange={(e) => setStudentForm({...studentForm, section: e.target.value})}>
                  <option value="A">Section A</option>
                  <option value="B">Section B</option>
                  <option value="C">Section C</option>
                </select>
                <input type="date" placeholder="Date of Birth *" value={studentForm.dob}
                  onChange={(e) => setStudentForm({...studentForm, dob: e.target.value})} required />
                <select value={studentForm.gender} onChange={(e) => setStudentForm({...studentForm, gender: e.target.value})}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <input type="tel" placeholder="Phone *" value={studentForm.phone}
                  onChange={(e) => setStudentForm({...studentForm, phone: e.target.value})} required />
                <input type="email" placeholder="Email" value={studentForm.email}
                  onChange={(e) => setStudentForm({...studentForm, email: e.target.value})} />
                <input type="date" placeholder="Admission Date" value={studentForm.admissionDate}
                  onChange={(e) => setStudentForm({...studentForm, admissionDate: e.target.value})} />
                <input type="text" placeholder="Blood Group" value={studentForm.bloodGroup}
                  onChange={(e) => setStudentForm({...studentForm, bloodGroup: e.target.value})} />
              </div>
              <textarea placeholder="Address" value={studentForm.address}
                onChange={(e) => setStudentForm({...studentForm, address: e.target.value})} />
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingItem ? 'Update' : 'Add'} Student
                </button>
                <button type="button" onClick={resetStudentForm} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teacher Modal */}
      {showTeacherModal && (
        <div className="modal-overlay" onClick={() => resetTeacherForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? 'Edit Teacher' : 'Add New Teacher'}</h2>
            <form onSubmit={handleTeacherSubmit}>
              <div className="form-grid">
                <input type="text" placeholder="Full Name *" value={teacherForm.name}
                  onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})} required />
                <select value={teacherForm.subject} onChange={(e) => setTeacherForm({...teacherForm, subject: e.target.value})} required>
                  <option value="">Select Subject *</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="text" placeholder="Qualification *" value={teacherForm.qualification}
                  onChange={(e) => setTeacherForm({...teacherForm, qualification: e.target.value})} required />
                <input type="text" placeholder="Experience" value={teacherForm.experience}
                  onChange={(e) => setTeacherForm({...teacherForm, experience: e.target.value})} />
                <input type="tel" placeholder="Phone *" value={teacherForm.phone}
                  onChange={(e) => setTeacherForm({...teacherForm, phone: e.target.value})} required />
                <input type="email" placeholder="Email *" value={teacherForm.email}
                  onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})} required />
                <input type="number" placeholder="Salary *" value={teacherForm.salary}
                  onChange={(e) => setTeacherForm({...teacherForm, salary: e.target.value})} required />
                <input type="date" placeholder="Join Date" value={teacherForm.joinDate}
                  onChange={(e) => setTeacherForm({...teacherForm, joinDate: e.target.value})} />
                <select value={teacherForm.gender} onChange={(e) => setTeacherForm({...teacherForm, gender: e.target.value})}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <textarea placeholder="Address" value={teacherForm.address}
                onChange={(e) => setTeacherForm({...teacherForm, address: e.target.value})} />
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingItem ? 'Update' : 'Add'} Teacher
                </button>
                <button type="button" onClick={resetTeacherForm} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Marks Modal - BULK ENTRY */}
      {showMarksModal && (
        <div className="modal-overlay" onClick={() => setShowMarksModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px'}}>
            <h2>Add Marks - Bulk Entry</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const studentId = parseInt(formData.get('studentId'));
              const exam = formData.get('exam');
              
              // Collect all records first
              const newRecords = [];
              subjects.forEach(subject => {
                const marksObtained = formData.get(`marks_${subject}`);
                const totalMarks = formData.get(`total_${subject}`);
                
                if (marksObtained && totalMarks && marksObtained !== '' && totalMarks !== '') {
                  const percentage = ((parseInt(marksObtained) / parseInt(totalMarks)) * 100).toFixed(2);
                  const record = {
                    id: Date.now() + Math.random(), // Unique ID
                    studentId,
                    subject,
                    exam,
                    marksObtained: parseInt(marksObtained),
                    totalMarks: parseInt(totalMarks),
                    percentage: percentage,
                    grade: calculateGrade(parseFloat(percentage)),
                    date: new Date().toISOString().split('T')[0]
                  };
                  newRecords.push(record);
                }
              });
              
              if (newRecords.length > 0) {
                // Save all records at once
                const updated = [...marks, ...newRecords];
                setMarks(updated);
                saveData('sms_marks', updated);
                alert(`✅ ${newRecords.length} subject marks saved successfully!`);
                setShowMarksModal(false);
                e.target.reset();
              } else {
                alert('❌ Please enter marks for at least one subject!');
              }
            }}>
              <div style={{marginBottom: '20px'}}>
                <select name="studentId" required style={{marginBottom: '10px', width: '100%'}}>
                  <option value="">Select Student *</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.rollNo} - {s.name} ({s.class})</option>
                  ))}
                </select>
                <select name="exam" required style={{width: '100%'}}>
                  <option value="">Select Exam *</option>
                  <option value="Unit Test 1">Unit Test 1</option>
                  <option value="Unit Test 2">Unit Test 2</option>
                  <option value="Mid Term">Mid Term</option>
                  <option value="Final">Final</option>
                </select>
              </div>

              <h3 style={{marginBottom: '15px', color: '#333'}}>Enter Marks for All Subjects:</h3>
              
              <div style={{maxHeight: '400px', overflowY: 'auto', marginBottom: '20px'}}>
                <table className="data-table" style={{fontSize: '14px'}}>
                  <thead>
                    <tr>
                      <th style={{width: '30%'}}>Subject</th>
                      <th style={{width: '35%'}}>Marks Obtained</th>
                      <th style={{width: '35%'}}>Total Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map(subject => (
                      <tr key={subject}>
                        <td style={{fontWeight: '600'}}>{subject}</td>
                        <td>
                          <input 
                            type="number" 
                            name={`marks_${subject}`}
                            placeholder="e.g. 85"
                            min="0"
                            style={{width: '100%', padding: '8px'}}
                          />
                        </td>
                        <td>
                          <input 
                            type="number" 
                            name={`total_${subject}`}
                            placeholder="e.g. 100"
                            min="1"
                            style={{width: '100%', padding: '8px'}}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                <p style={{margin: 0, fontSize: '14px', color: '#666'}}>
                  💡 <strong>Tip:</strong> You can enter marks for all subjects at once, or just fill in a few. Empty fields will be skipped.
                </p>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">💾 Save All Marks</button>
                <button type="button" onClick={() => setShowMarksModal(false)} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fee Modal */}
      {showFeeModal && (
        <div className="modal-overlay" onClick={() => setShowFeeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Collect Fee Payment</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleFeePayment(
                parseInt(formData.get('studentId')),
                formData.get('amount'),
                formData.get('month'),
                formData.get('paymentMode')
              );
              setShowFeeModal(false);
              e.target.reset();
            }}>
              <select name="studentId" required>
                <option value="">Select Student *</option>
                {students.map(s => {
                  const status = getFeeStatus(s.id);
                  return (
                    <option key={s.id} value={s.id}>
                      {s.rollNo} - {s.name} (Balance: ₹{status.balance})
                    </option>
                  );
                })}
              </select>
              <input type="number" name="amount" placeholder="Amount *" required />
              <input type="month" name="month" placeholder="Month" required />
              <select name="paymentMode" required>
                <option value="">Payment Mode *</option>
                <option value="Cash">Cash</option>
                <option value="Online">Online</option>
                <option value="Cheque">Cheque</option>
              </select>
              <div className="modal-actions">
                <button type="submit" className="btn-primary">Collect Payment</button>
                <button type="button" onClick={() => setShowFeeModal(false)} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;